import React, { useEffect } from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { NavigationBar, NavigationItem } from '.';
import { useDispatch, useSelector } from 'react-redux';
import { actions as dashboardFilterActions } from '../../../slices/filters/dashboardFilters';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { IIdentifier, PermissionAccessTypes } from '../../../slices/accounts/account';
import { RootState } from '../../../slices/rootReducer';
import { AccessTypes } from '../domain/accountAccessType';
import { getCurrentRenewal } from '../../../slices/accounts/currentRenewal/currentRenewal.thunk';
import { isExternalClient, isMetaClient, isPureProfileClient } from '../../../utils/clients';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { PERMISSION } from '../../../slices/accounts/accountSelectors';

const SETTINGS_ROUTE = 'settings';
const USERS_ROUTE = 'users';
const BILLING_ROUTE = 'billing';
const INVOICES_ROUTE = 'invoices';
const QUESTION_PACKAGES_ROUTE = 'questionPackages';
const API_KEY_ROUTE = 'apiKey';
const SSO_ROUTE = 'sso';
const VIDEO_TRACKING_ROUTE = 'video';
export const CREDITS_PATH = 'credits';

const navigationRoutes = [
  {
    label: 'Settings',
    route: SETTINGS_ROUTE
  },
  {
    label: 'Users',
    route: USERS_ROUTE
  },
  {
    label: 'Billing',
    route: BILLING_ROUTE
  },
  {
    label: 'Invoices',
    route: INVOICES_ROUTE
  },
  {
    label: 'Credits',
    route: CREDITS_PATH
  },
  {
    label: 'Video',
    route: VIDEO_TRACKING_ROUTE
  },
  {
    label: 'API Key',
    route: API_KEY_ROUTE
  },
  {
    label: 'SSO',
    route: SSO_ROUTE
  },
  {
    label: 'Question Packages',
    route: QUESTION_PACKAGES_ROUTE
  }
];

interface INavigationBarProps {
  displayBillingTab: boolean;
}

export const AccountsNavigationBar: React.FC<INavigationBarProps> = (props: INavigationBarProps) => {
  const match = useRouteMatch<IIdentifier>();
  const { id = '' } = match.params;
  const account = useSelector((state: RootState) => accountListSelectors.getAccountById(state, id));
  const dispatch = useDispatch();
  const accountId = account?.id;
  const hasInvoicedDeal = !isExternalClient()
    ? account?.currentDeal?.invoiced ?? false
    : true;

  const videoQuestionStatus = account?.permissions?.find((permission) => {
    return permission.name === PERMISSION.VideoQuestion;
  })?.accessLevel;

  useEffect(() => {
    dispatch(dashboardFilterActions.resetFilters());
  }, [dispatch]);

  useEffect(() => {
    if (accountId && !isExternalClient()) {
      dispatch(getCurrentRenewal({ accountId }));
    }
  }, [dispatch, accountId]);

  const url = match.url.split('/');
  url.pop();
  const baseURL = url.join('/');
  const isEnterpriseAccount = account?.accessType === AccessTypes.Enterprise;
  const isOnBillingTab = match.url.includes(BILLING_ROUTE);
  const isTabDisabled = isEnterpriseAccount && account?.currentDeal !== undefined && !hasInvoicedDeal;

  if (!isOnBillingTab && isTabDisabled && !isExternalClient()) {
    return <Redirect to={`/accounts/${id}/${BILLING_ROUTE}`} />;
  }

  const allowedNavigationRoutes = (route: string) => {
    const excludedClientsHiddenTabs = [QUESTION_PACKAGES_ROUTE, SSO_ROUTE, CREDITS_PATH];
    const shouldExcludeClients = isPureProfileClient() || isMetaClient();
    if (shouldExcludeClients && excludedClientsHiddenTabs.includes(route)) {
      return false;
    }

    if ((!isFeatureEnabled(Features.VideoTracking) || videoQuestionStatus !== PermissionAccessTypes.Available) && route === VIDEO_TRACKING_ROUTE) {
      return false;
    }

    if (isExternalClient()) {
      const netquestHiddenTabs = [BILLING_ROUTE, INVOICES_ROUTE];
      return !netquestHiddenTabs.includes(route);
    }

    const sightxHiddenTabs = [QUESTION_PACKAGES_ROUTE];
    return !sightxHiddenTabs.includes(route);
  };

  const navItems: NavigationItem[] = navigationRoutes
    .filter(item => allowedNavigationRoutes(item.route))
    .map(item => ({
      label: item.label,
      path: `${baseURL}/${item.route}`,
      key: item.route,
      disabled: item.route !== BILLING_ROUTE && isTabDisabled
    }));

  if (!props.displayBillingTab) {
    navItems.splice(2, 1);
  }

  return (
    <NavigationBar navItems={navItems} />
  );
};
